/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { Range, RangeWithKey } from "react-date-range";
import styled, { css } from "styled-components";
import { Text, Datepicker, DayPicker, theme, Button } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { InlineActionButton } from "~/components/Styled/InlineActionButton";
import { dateFormat } from "~/constants";
import { useClickOutside } from "~/tools";

type AuditInlineDatePickerProps = {
  date: Dayjs | null;
  setDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  left?: boolean;
  canEdit?: boolean;
};

type InnerType = {
  left: boolean;
};

const Wrap = styled.div`
  position: relative;
`;

const Inner = styled.div<InnerType>`
  background-color: ${theme.color.background.ui01};
  position: absolute;
  z-index: ${theme.zindex.popup};
  filter: drop-shadow(0 1.5rem 1.5rem rgba(0, 0, 0, 0.2));
  ${({ left }) =>
    left &&
    css`
      right: -10rem;
    `}
`;

function AuditInlineDatePicker({
  date,
  setDate,
  isEditMode,
  setIsEditMode,
  left,
  canEdit = true,
}: AuditInlineDatePickerProps) {
  dayjs.extend(utc);

  const clearDate = () => {
    setDate(null);
    setIsEditMode(false);
  };

  const wrapperRef = React.useRef(null);

  useClickOutside(
    wrapperRef,
    () => {
      setIsEditMode(() => false);
    },
    isEditMode
  );

  function EditForm() {
    return (
      <FlexContainer direction="column" align="flex-start" ref={wrapperRef}>
        <Datepicker>
          <DayPicker
            selected={date ? dayjs(date).toDate() : dayjs(new Date()).toDate()}
            // @ts-ignore
            onDayClick={(newD: Range | { selection: RangeWithKey } | Date) => {
              if ("getFullYear" in newD) {
                const newDate = dayjs
                  .utc()
                  .year(newD.getFullYear())
                  .month(newD.getMonth())
                  .date(newD.getDate())
                  .hour(0)
                  .minute(0)
                  .second(0)
                  .millisecond(0);
                setDate(newDate);
              }
              setIsEditMode(false);
            }}
          />
        </Datepicker>
        <Button
          type="button"
          mt="0.5rem"
          icon="trash"
          size="md"
          severity="low"
          onClick={clearDate}
        >
          Clear
        </Button>
      </FlexContainer>
    );
  }

  function DisplayText() {
    return (
      <>
        <Text tag="span" css={{ minHeight: "24px", minWidth: "9rem" }}>
          {date ? dayjs(date).format(`${dateFormat}`) : "-"}
        </Text>
        {canEdit && (
          <InlineActionButton
            type="button"
            ml="0.5rem"
            icon="edit"
            size="sm"
            severity="low"
            onClick={() => setIsEditMode(true)}
          />
        )}
        <Wrap>
          <Inner left={!!left}>{isEditMode && <EditForm />}</Inner>
        </Wrap>
      </>
    );
  }

  return <FlexContainer align="center">{DisplayText()}</FlexContainer>;
}

export { AuditInlineDatePicker };
